import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

const SKIP_GLOBAL_ERROR_DIALOG_HEADER = 'X-Skip-Global-Error-Dialog';

export interface ErpLeaveImportApprovedRequest {
  id: number;
  schedule_submission_id: number;
  academic_term_id: number;
  leave_date: string;
  status: string;
  subject_code: string | null;
  subject_name: string | null;
  makeup_teaching_date: string | null;
  makeup_start_time: string | null;
  makeup_end_time: string | null;
  leave_type_name: string | null;
  leave_period_name: string | null;
  reason?: string;
}

export interface ErpLeaveImportRowResult {
  row: number | null;
  leave_record_id?: number | null;
  academic_term_id: number;
  academic_term_name: string;
  academic_term_year: number;
  schedule_submission_id: number;
  leave_date: string;
  erp_person_number?: string | null;
  erp_person_name?: string | null;
  erp_full_name?: string | null;
  erp_department?: string | null;
  erp_leave_start_date?: string | null;
  erp_leave_end_date?: string | null;
  erp_submitted_at?: string | null;
  erp_last_updated_at?: string | null;
  erp_approval_status?: string | null;
  erp_adjustment_status?: string | null;
  leave_type: string;
  is_active: boolean | null;
  reason?: string;
  approved_requests?: ErpLeaveImportApprovedRequest[];
}

export interface ErpLeaveImportTeacherSummary {
  user_id: number;
  full_name: string;
  email: string;
  matched: ErpLeaveImportRowResult[];
  import_unmatched: ErpLeaveImportRowResult[];
  system_unmatched: ErpLeaveImportApprovedRequest[];
}

export interface ErpLeaveImportMatchSummary {
  teacher_count: number;
  matched_count: number;
  import_unmatched_count: number;
  system_unmatched_count: number;
  teachers: ErpLeaveImportTeacherSummary[];
}

export interface ErpLeaveImportResponse {
  success: boolean;
  message: string;
  saved_count: number;
  match_summary: ErpLeaveImportMatchSummary;
}

export interface ErpLeaveImportResultRow {
  id: number;
  sequence?: number;
  academic_term_id: number;
  academic_term_name: string | null;
  academic_term_year: number | null;
  user_id: number;
  full_name: string;
  email: string;
  matched_count: number;
  import_unmatched_count: number;
  system_unmatched_count: number;
  system_matched_request_count: number;
  system_unmatched_request_count: number;
  system_request_count: number;
  total_count: number;
  imported_at: string | null;
  imported_by_id: number | null;
  imported_by_name: string | null;
}

export interface ErpLeaveImportResultDetail extends ErpLeaveImportResultRow {
  matched: ErpLeaveImportRowResult[];
  import_unmatched: ErpLeaveImportRowResult[];
  system_unmatched: ErpLeaveImportApprovedRequest[];
}

export interface ErpLeaveImportResultsResponse {
  data: ErpLeaveImportResultRow[];
  page: number;
  limit: number;
  total: number;
  total_pages?: number;
  total_page: number;
  offset?: number;
}

export interface ErpLeaveImportResultsParams {
  page?: number;
  limit?: number;
  keywords?: string;
  sort?: string | null;
  direction?: string | null;
}

export interface ErpLeaveImportLeaveRecordRow {
  id: number;
  sequence?: number;
  schedule_submission_id: number;
  academic_term_id: number;
  academic_term_name: string | null;
  academic_term_year: number | null;
  user_id: number;
  full_name: string;
  email: string;
  leave_date: string | null;
  erp_person_number: string | null;
  erp_person_name: string | null;
  erp_full_name: string | null;
  erp_department: string | null;
  erp_leave_start_date: string | null;
  erp_leave_end_date: string | null;
  erp_submitted_at: string | null;
  erp_last_updated_at: string | null;
  erp_approval_status: string | null;
  erp_adjustment_status: string | null;
  leave_type: string;
  is_active: boolean | null;
  source: string;
  imported_by_id: number | null;
  imported_by_name: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ErpLeaveImportLeaveRecordsResponse {
  data: ErpLeaveImportLeaveRecordRow[];
  page: number;
  limit: number;
  total: number;
  total_pages?: number;
  total_page: number;
  offset?: number;
}

export interface ErpLeaveImportLeaveRecordsParams {
  page?: number;
  limit?: number;
  academic_term_id?: number | null;
  academic_term_name?: string | null;
  academic_term_year?: number | null;
  keywords?: string;
  sort?: string | null;
  direction?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class ErpLeaveImportService {
  private apiUrl = '/erp_leave_import';

  #http = inject(HttpClient);

  exportTemplate(): Observable<Blob> {
    return this.#http.get(`${this.apiUrl}/export_template`, {
      responseType: 'blob',
    });
  }

  getResults(params: ErpLeaveImportResultsParams = {}): Observable<ErpLeaveImportResultsResponse> {
    return this.#http.get<ErpLeaveImportResultsResponse>(`${this.apiUrl}/results`, {
      params: this.compactParams(params),
    });
  }

  getResult(id: number): Observable<ErpLeaveImportResultDetail> {
    return this.#http.get<ErpLeaveImportResultDetail>(`${this.apiUrl}/results/${id}`);
  }

  getLeaveRecords(
    params: ErpLeaveImportLeaveRecordsParams = {},
  ): Observable<ErpLeaveImportLeaveRecordsResponse> {
    return this.#http.get<ErpLeaveImportLeaveRecordsResponse>(`${this.apiUrl}/leave_records`, {
      params: this.compactParams(params),
    });
  }

  importLeaveRecords(file: File): Observable<ErpLeaveImportResponse> {
    const formData = new FormData();

    formData.append('file', file);

    return this.#http.post<ErpLeaveImportResponse>(`${this.apiUrl}/import`, formData, {
      headers: {
        [SKIP_GLOBAL_ERROR_DIALOG_HEADER]: 'true',
      },
    });
  }

  private compactParams(params: ErpLeaveImportResultsParams | ErpLeaveImportLeaveRecordsParams): Record<string, string> {
    return Object.entries(params).reduce<Record<string, string>>((result, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        result[key] = String(value);
      }

      return result;
    }, {});
  }
}
